import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useContext,
    useState,
} from 'react'
import { CoinExchangeType, CoinInfo, useCoin } from './coin_context'
import { IvaEnum, ProductInfo } from './product_context'
import { useSnack } from './snack_context'
import { SupplierInfo } from './supplier_context'
import { useLoading } from './loading_context'
import { createBuy, getBuyById, updateBuy } from '../services/buy'
import { calculateSalePrices } from '../services/product'
import { useNavigate } from 'react-router-dom'
import { AccountInfo, useAccount } from './account_context'
import { CashierClosedBuyError } from '../services/buy'
import { getDateInIsoFormat, getExchangeValue, getProductStockInBranch, getTotalIvaCalculated } from '../utils/maks'
import { CashierInfo, useCashier } from './cashier_context'
import { BranchInfo } from './branch_context'
import { ChangeInBuy, SystemBranchEnum, useConfigs } from './configs_context'
import { PaymentMethodInfo } from './payment_method_context'
import { prod } from '@tensorflow/tfjs-core'
import { CreditNoteInfo } from './sale_context'
import { CheckInfo } from '../pages/Bank/CheckHome'
import { loadProductsFromExcel } from '../services/product'
const currency = require('currency.js')

export interface ExchangeInfo {
    coin: CoinInfo
    exchange: number
    amount: number
    flagReverseExchange: number
}

export interface ProductInBuy {
    id?: number
    product: ProductInfo
    price: number
    cifPrice: number
    porcentGain: number
    salePrice: number
    exchange: ExchangeInfo | null
    qnt: number
    discount: number
    subtotal: number
    returnedQnt?: number
}

export interface PaymentInBuy {
    id?: number
    paymentCoin: CoinInfo | null
    changeCoin: CoinInfo | null
    amountPayed: number
    paymentMethod: PaymentMethodInfo | null
    madeBy: AccountInfo | null
    date?: string
    hour?: string
    cashier?: CashierInfo
    exchange?: {
        exchangeValue: number
        exchangeAmountPayed: number
        flagReverseExchange: number
    }
    proofText?: string
    proofImage?: string
    isSummerTime?: boolean
    exchangeChange: number | null
    change: number
    paymentStatus?: number
    cashierDiscounted: boolean
    type: number
    check?: CheckInfo | null
    creditDiscount: number
    buy?: BuyInfo
}

export enum PaymentBuyType {
    Normal = 0,
    Costs = 1,
}

export enum PaymentType {
    Normal = 0,
    Credit = 1,
    Parcel = 2,
}

export enum BuyStatus {
    Open = 0,
    Closed = 1,
    Canceled = 2,
}

export enum BuyPaymentStatus {
    Pending = 0,
    Created = 1,
    Canceled = 2,
}

export const BuyStatusLabel: Record<number, string> = {
    0: 'Abierta',
    1: 'Cerrada',
    2: 'Cancelada',
}

export const BuyPaymentStatusLabel: Record<number, string> = {
    0: 'Pendiente',
    1: 'Cerrada',
    2: 'Cancelada',
}

export const ConsignationStatusLabel: Record<number, string> = {
    0: 'Sin consigniación',
    1: 'Consigniación pendiente',
    2: 'Consigniación finalizada',
}

export const PaymentTypeLabel: Record<number, string> = {
    0: 'Contado',
    1: 'Crédito',
    2: 'Parcelado',
}

export const PaymentBuyTypeLabel: Record<number, string> = {
    0: 'Normal',
    1: 'Costos',
}

export enum ConsignationStatus {
    NoConsignation = 0,
    PendingConsignation = 1,
    EndedConsignation = 2,
}

export enum BuyType {
    Normal = 0,
    Order = 1,
}

export interface BuyInfo {
    id?: number
    bill: string
    products: ProductInBuy[]
    selectedProducts?: ProductInBuy[]
    supplier: SupplierInfo | null
    total: number
    totalAmountPayed: number
    totalAmountPayedCost: number
    totalIva: number
    paymentType: number
    discount: number
    payments: PaymentInBuy[]
    branch: BranchInfo | null
    date?: string
    hour?: string
    coin?: CoinInfo
    cashier?: CashierInfo
    account?: AccountInfo
    productCount?: number
    isSummerTime?: boolean
    paymentStatus?: number
    dispatch: number
    freight: number
    discharge: number
    manualTotal: number
    consignationDate: Date | null
    consignationStatus?: number
    isEditing?: boolean
    creditNotes?: CreditNoteInfo[]
    type: number
    orderId?: number | null
}

export interface BuyReportsInfo {
    coinId: number
    totalPurchased: number
    totalPaid: number
    totalPurchases: number
    totalCosts: number
    totalPaidCosts: number
    totalInCreditNotes: number
    coin?: CoinInfo
}

export interface BuyedProductsInfo {
    product: string
    productId: number
    averageBuyPrice: number
    qntBuyed: number
    qntBuys: number
    coin?: CoinInfo
}

export interface ProductInConsignation {
    id: number
    qnt: number
    productInBuy: ProductInBuy
}

export interface ConsignationInfo {
    id?: number
    products: ProductInConsignation[]
    total: number
    date?: string
    hour?: string
    cashier?: Partial<CashierInfo>
    account?: Partial<AccountInfo>
    isSummerTime?: boolean
}

const buyInfoDefault: BuyInfo = {
    bill: '',
    products: [],
    supplier: null,
    total: 0,
    totalAmountPayed: 0,
    totalAmountPayedCost: 0,
    totalIva: 0,
    discount: 0,
    paymentType: 0,
    payments: [],
    branch: null,
    dispatch: 0,
    freight: 0,
    discharge: 0,
    manualTotal: 0,
    consignationDate: null,
    isEditing: false,
    type: BuyType.Normal,
}

export const getSupplierLabel = (supplier: SupplierInfo) => {
    return `${supplier.name}, email: ${supplier.email}, ruc: ${supplier.document}`
}

export interface BuyContextInterface {
    buyInfo: BuyInfo
    setBuyInfo: Dispatch<SetStateAction<BuyInfo>>
    handleChangeBill(bill: string): void
    handleAddNewProducts(products: ProductInfo[]): void
    handleRemoveProduct(product: ProductInBuy): void
    handleChangeProductQnt(product: ProductInBuy, qnt: number): void
    handleChangeProductDiscount(product: ProductInBuy, discount: number): void
    handleChangeProductPrice(product: ProductInBuy, price: number): void
    handleChangeProductSubtotal(product: ProductInBuy, subtotal: number): void
    handleChangeProductExchange(
        product: ProductInBuy,
        exchange: ExchangeInfo
    ): void
    handleEditProduct(productInBuy: ProductInBuy, product: ProductInfo): void
    handleChangeSupplier(supplier: SupplierInfo | null): void
    handleChangePayment(paymentType: number, payment: PaymentInBuy): void
    handleChangeBranch(branch: BranchInfo): void
    validate(): boolean
    handleCreateNewBuy(
        paymentType?: number,
        payment?: PaymentInBuy
    ): Promise<void>
    handleChangeSelectedProducts(products: ProductInBuy[]): void
    resetContext(type: number): void
    editBuy(id: number): Promise<void>
    handleCalculatePriceWithStock(): void
    handleCalculatePriceWithCost(): void
    handleCalculateSalePrices(): Promise<void>
    handleChangeCoin(coin: CoinInfo): void
    handleLoadExcelInPreSale(base64: string): void
}

const buyContext = createContext<BuyContextInterface>({
    buyInfo: buyInfoDefault,
    setBuyInfo: () => {},
    handleChangeBill: () => {},
    handleAddNewProducts: () => {},
    handleRemoveProduct: () => {},
    handleChangeProductQnt: () => {},
    handleChangeProductDiscount: () => {},
    handleChangeProductPrice: () => {},
    handleChangeProductSubtotal: () => {},
    handleChangeProductExchange: () => {},
    handleEditProduct: () => {},
    handleChangeSupplier: () => {},
    handleChangePayment: () => {},
    handleChangeBranch: () => {},
    validate: () => false,
    handleCreateNewBuy: async () => {},
    handleChangeSelectedProducts: () => {},
    resetContext: () => {},
    editBuy: () => Promise.resolve(),
    handleCalculatePriceWithStock: () => {},
    handleCalculatePriceWithCost: () => {},
    handleCalculateSalePrices: () => Promise.resolve(),
    handleChangeCoin: () => {},
    handleLoadExcelInPreSale: () => {},
})

export function useBuy() {
    return useContext(buyContext)
}

interface Props {
    children: ReactNode
}

export const BuyContextProvider = ({ children }: Props) => {
    const loading = useLoading()
    const snack = useSnack()
    const navigate = useNavigate()
    const { coins } = useCoin()
    const [buyInfo, setBuyInfo] = useState<BuyInfo>(buyInfoDefault)
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()
    const { handleChangeCashierFromList } = useCashier()

    const getTotal = (products: ProductInBuy[]) => {
        return products
            .map((p) => p.subtotal)
            .reduce((total, current) => {
                return currency(total).add(current).value
            }, 0)
    }

    const getTotalIva = (products: ProductInBuy[]) => {
        return getTotalIvaCalculated(
            products.map((p) => ({
                iva: p.product.iva,
                proporcao: p.product.proporcaoIva,
                preco: p.price,
                quantidade: p.qnt,
            }))
        )
    }

    const calculateExchange = (
        product: ProductInBuy,
        coin: CoinInfo
    ): ProductInBuy => {
        if (product.product.coin?.id === coin.id) return product

        const exchange = coin.exchanges.find(
            (e) => e.coin.id === product.product.coin?.id
        )

        return {
            ...product,
            price: getExchangeValue({
                amount: product.price,
                exchange: Number(exchange?.buyExchange ?? 0),
                coinExchangeType: exchange?.flagReverseExchange ?? 0,
                order: 'reverse',
            }),
            cifPrice: getExchangeValue({
                amount: product.cifPrice,
                exchange: Number(exchange?.buyExchange ?? 0),
                coinExchangeType: exchange?.flagReverseExchange ?? 0,
                order: 'reverse',
            }),
            salePrice: getExchangeValue({
                amount: product.salePrice,
                exchange: Number(exchange?.buyExchange ?? 0),
                coinExchangeType: exchange?.flagReverseExchange ?? 0,
                order: 'reverse',
            }),
            subtotal: getExchangeValue({
                amount: product.subtotal,
                exchange: Number(exchange?.buyExchange ?? 0),
                coinExchangeType: exchange?.flagReverseExchange ?? 0,
                order: 'reverse',
            }),
        }
    }

    const handleChangeBill = (bill: string) => {
        setBuyInfo((prev) => ({
            ...prev,
            bill: bill,
        }))
    }

    const handleAddNewProducts = (products: ProductInfo[]) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch
        if (isMasterBranch && !buyInfo.branch) {
            snack.error('Seleccione una sucursal!')
            return
        }

        const newProducts = [...buyInfo.products]
        let count = buyInfo.productCount ?? 0

        products.forEach((product) => {
            const foundProduct = buyInfo.products.find(
                (p) => p.product.id === product.id
            )
            if (foundProduct && foundProduct.product.autoSum) {
                handleChangeProductQnt(
                    foundProduct,
                    currency(foundProduct.qnt).add(1).value
                )
                return
            }
            if (!product.isAtive) {
                snack.error('O produto está desativado!')
                return
            }

            count += 1
            newProducts.push(
                calculateExchange(
                    {
                        product: product,
                        price: product.buyPrice,
                        cifPrice: 0,
                        exchange: null,
                        qnt: 1,
                        subtotal: product.buyPrice,
                        salePrice: product.salePrice,
                        porcentGain: product.porcentGain,
                        discount: 0,
                        id: count,
                    },
                    buyInfo.coin!
                )
            )
        })

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
            productCount: count,
        }))
    }

    const handleRemoveProduct = (product: ProductInBuy) => {
        const newProducts = [...buyInfo.products]
        const index = newProducts.findIndex((p) => p.id === product.id)
        if (index === -1) return

        newProducts.splice(index, 1)

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductQnt = (product: ProductInBuy, qnt: number) => {
        const newProducts = [...buyInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.qnt = qnt
        newProduct.subtotal = newProduct.qnt * newProduct.price

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductDiscount = (
        product: ProductInBuy,
        discount: number
    ) => {
        const newProducts = [...buyInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.discount = discount

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductPrice = (product: ProductInBuy, price: number) => {
        const newProducts = [...buyInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.price = price

        const costWithCif = currency(newProduct.price).add(
            newProduct.cifPrice
        ).value

        if (configs.changeInBuy === ChangeInBuy.SalePrice) {
            newProduct.salePrice = currency(costWithCif).add(
                currency(costWithCif)
                    .multiply(newProduct.porcentGain)
                    .divide(100)
            ).value
        } else {
            newProduct.porcentGain = currency(newProduct.salePrice)
                .subtract(costWithCif)
                .multiply(100)
                .divide(costWithCif).value
        }

        newProduct.exchange = null
        newProduct.subtotal = newProduct.qnt * newProduct.price

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductSubtotal = (
        product: ProductInBuy,
        subtotal: number
    ) => {
        const newProducts = [...buyInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.subtotal = subtotal
        newProduct.price = newProduct.subtotal / newProduct.qnt

        const costWithCif = currency(newProduct.price).add(
            newProduct.cifPrice
        ).value

        if (configs.changeInBuy === ChangeInBuy.SalePrice) {
            newProduct.salePrice = currency(costWithCif).add(
                currency(costWithCif)
                    .multiply(newProduct.porcentGain)
                    .divide(100)
            ).value
        } else {
            newProduct.porcentGain = currency(newProduct.salePrice)
                .subtract(costWithCif)
                .multiply(100)
                .divide(costWithCif).value
        }

        newProduct.exchange = null

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductExchange = (
        product: ProductInBuy,
        exchange: ExchangeInfo
    ) => {
        const newProducts = [...buyInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.exchange = exchange
        newProduct.price = getExchangeValue({
            amount: newProduct.exchange.amount,
            exchange: newProduct.exchange.exchange,
            coinExchangeType: exchange.flagReverseExchange,
            order: 'reverse',
        })
        newProduct.subtotal = newProduct.qnt * newProduct.price

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleEditProduct = (
        productInBuy: ProductInBuy,
        product: ProductInfo
    ) => {
        const newProducts = [...buyInfo.products]
        const foundProduct = newProducts.find((p) => p.id === productInBuy.id)
        if (!foundProduct) return

        foundProduct.exchange = null
        foundProduct.price = product.buyPrice
        foundProduct.salePrice = product.salePrice
        foundProduct.porcentGain = product.porcentGain
        foundProduct.subtotal = foundProduct.price * foundProduct.qnt
        foundProduct.product = product

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeSupplier = (supplier: SupplierInfo | null) => {
        if (supplier && !supplier.isActive) {
            snack.error('O forncedor não está ativo!')
            return
        }

        setBuyInfo((prev) => ({
            ...prev,
            supplier: supplier,
        }))
    }

    const handleChangePayment = (
        paymentType: number,
        payment: PaymentInBuy
    ) => {
        setBuyInfo((prev) => ({
            ...prev,
            paymentType: paymentType,
            payments: [payment],
        }))
    }

    const handleChangeBranch = (branch: BranchInfo) => {
        setBuyInfo((prev) => ({
            ...prev,
            branch: branch,
            products: [],
            total: 0,
            totalIva: 0,
            coin: branch.coin,
        }))
        if (configs.chooseCashier) {
            handleChangeCashierFromList(branch.id)
        }
    }

    const validate = (): boolean => {
        if (!buyInfo.supplier) {
            snack.error('Selecione um fornecedor')
            return false
        }
        if (!buyInfo.bill) {
            snack.error('Selecione uma fatura')
            return false
        }
        if (!buyInfo.products.length) {
            snack.error('Selecione um produto!')
            return false
        }
        if (
            buyInfo.products.filter((p) => !p.price || !p.salePrice || !p.qnt)
                .length
        ) {
            snack.error('A quantidade e o valor não podem ser 0!')
            return false
        }
        if (
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch &&
            !buyInfo.branch
        ) {
            snack.error('Selecione uma filial!')
            return false
        }
        return true
    }

    const handleCreateNewBuy = async (
        paymentType?: number,
        payment?: PaymentInBuy
    ) => {
        loading.show()
        try {
            if (buyInfo.isEditing)
                await updateBuy({
                    id_compra: buyInfo.id as number,
                    produtos: buyInfo.products.map((p) => ({
                        id_produto: p.product?.id ?? 0,
                        preco_compra: p.price,
                        preco_cif: p.cifPrice,
                        preco_venda: p.salePrice,
                        quantidade_produto: p.qnt,
                        porc_lucro: p.porcentGain,
                        desconto: p.discount,
                        exchange: p.exchange
                            ? {
                                  id_moeda_estrangeira: p.exchange.coin.id ?? 0,
                                  valor_moeda_estrangeira: p.exchange.amount,
                                  cambio: p.exchange.exchange,
                                  flag_cambio_reverso:
                                      p.exchange.flagReverseExchange,
                              }
                            : undefined,
                    })),
                    frete: buyInfo.freight,
                    despacho: buyInfo.dispatch,
                    descarga: buyInfo.discharge,
                    desconto: buyInfo.discount,
                })
            else {
                await createBuy({
                    flag_forma_pagamento: paymentType ?? 0,
                    id_fornecedor: buyInfo.supplier?.id ?? 0,
                    numero_fatura: buyInfo.bill,
                    produtos: buyInfo.products.map((p) => ({
                        id_produto: p.product?.id ?? 0,
                        preco_compra: p.price,
                        preco_cif: p.cifPrice,
                        preco_venda: p.salePrice,
                        quantidade_produto: p.qnt,
                        porc_lucro: p.porcentGain,
                        desconto: p.discount,
                        exchange: p.exchange
                            ? {
                                  id_moeda_estrangeira: p.exchange.coin.id ?? 0,
                                  valor_moeda_estrangeira: p.exchange.amount,
                                  cambio: p.exchange.exchange,
                                  flag_cambio_reverso:
                                      p.exchange.flagReverseExchange,
                              }
                            : undefined,
                    })),
                    purchase_payment:
                        payment &&
                        (payment.amountPayed || payment.creditDiscount)
                            ? {
                                  id_metodo_pagamento:
                                      payment.paymentMethod?.id ?? 0,
                                  id_moeda: payment.paymentCoin?.id ?? 0,
                                  id_moeda_troco: payment.changeCoin?.id ?? 0,
                                  valor_pagamento: payment.amountPayed,
                                  comprovante_imagem: payment.proofImage,
                                  comprovante_texto: payment.proofText,
                                  flag_descontar_caixa: Number(
                                      payment.cashierDiscounted
                                  ),
                                  tipo: PaymentBuyType.Normal,
                                  desconto_credito: payment.creditDiscount,
                              }
                            : null,
                    id_filial_master: buyInfo.branch?.id,
                    frete: buyInfo.freight,
                    despacho: buyInfo.dispatch,
                    descarga: buyInfo.discharge,
                    total_manual: buyInfo.manualTotal,
                    desconto: buyInfo.discount,
                    data_consignacao: buyInfo.consignationDate
                        ? getDateInIsoFormat(buyInfo.consignationDate)
                        : null,
                    flag_tipo: buyInfo.type,
                    id_pedido: buyInfo.orderId,
                    id_cheque: payment?.check?.id,
                    data_pagamento_cheque: payment?.check?.paymentDate
                        ? getDateInIsoFormat(payment.check?.paymentDate)
                        : undefined,
                    id_moeda: buyInfo.coin?.id ?? 0,
                })
            }

            snack.success(
                `Compra ${
                    !buyInfo.isEditing ? 'criada' : 'atualizada'
                } com sucesso!`
            )
            navigate(
                buyInfo.type === BuyType.Normal
                    ? '/system/buy'
                    : '/system/buy/order'
            )
            if (!buyInfo.isEditing)
                localStorage.removeItem(`buyInfo-${buyInfo.type}`)
            setBuyInfo({ ...buyInfoDefault })
        } catch (error) {
            if (error instanceof CashierClosedBuyError)
                snack.error('O caixa está fechado!')
            else snack.connectionFail(error)
        }
        loading.hide()
    }

    const resetContext = (type: number) => {
        setBuyInfo({
            ...buyInfoDefault,
            type,
            bill: type === BuyType.Order ? '---' : '',
        })
    }

    const editBuy = async (id: number) => {
        try {
            const buy = await getBuyById(id, coins)

            buy.products.forEach((p, i) => {
                p.subtotal = p.price * p.qnt
                p.id = i + 1
            })

            setBuyInfo(() => ({
                ...buy,
                total: getTotal(buy.products),
                totalIva: getTotalIva(buy.products),
                isEditing: true,
                id,
                productCount: buy.products.length,
            }))
        } catch (error) {
            console.log(error)
            snack.error('Compra não encontrada')
            throw new Error('Purchase not found')
        }
    }

    const handleChangeSelectedProducts = (products: ProductInBuy[]) => {
        setBuyInfo((prev) => ({
            ...prev,
            selectedProducts: products,
        }))
    }

    const handleCalculatePriceWithStock = () => {
        const newProducts = [...buyInfo.products]

        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch

        if (isMasterBranch && !buyInfo.branch) {
            snack.error('Seleccione una sucursal!')
            return
        }

        for (const product of newProducts) {
            const currentStock = getProductStockInBranch(
                product.product,
                buyInfo.branch,
                isMasterBranch
            )

            const currentPrice = product.product.buyPrice

            const newBuyPrice = currency(product.price * product.qnt)
                .add(currentPrice * currentStock)
                .divide(currency(product.qnt).add(currentStock)).value

            product.price = newBuyPrice

            const costWithCif = currency(product.price).add(
                product.cifPrice
            ).value

            if (configs.changeInBuy === ChangeInBuy.SalePrice) {
                product.salePrice = currency(costWithCif).add(
                    currency(costWithCif)
                        .multiply(product.porcentGain)
                        .divide(100)
                ).value
            } else {
                product.porcentGain = currency(product.salePrice)
                    .subtract(costWithCif)
                    .multiply(100)
                    .divide(costWithCif).value
            }

            product.subtotal = currency(product.price).multiply(
                product.qnt
            ).value
        }

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleCalculatePriceWithCost = () => {
        const newProducts = [...buyInfo.products]

        const totalCost = currency(buyInfo.freight)
            .add(buyInfo.dispatch)
            .add(buyInfo.discharge)
            .subtract(buyInfo.discount).value

        for (const product of newProducts) {
            product.cifPrice = currency(product.price, { precision: 15 })
                .divide(buyInfo.total)
                .multiply(totalCost).value

            const costWithCif = currency(product.price).add(
                product.cifPrice
            ).value

            if (configs.changeInBuy === ChangeInBuy.SalePrice) {
                product.salePrice = currency(costWithCif).add(
                    currency(costWithCif)
                        .multiply(product.porcentGain)
                        .divide(100)
                ).value
            } else {
                product.porcentGain = currency(product.salePrice)
                    .subtract(costWithCif)
                    .multiply(100)
                    .divide(costWithCif).value
            }

            product.subtotal = currency(product.price).multiply(
                product.qnt
            ).value
        }

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleCalculateSalePrices = async () => {
        if (!buyInfo.products.length) return
        loading.logicShow()
        try {
            const calcResults = await calculateSalePrices(
                buyInfo.products.map((p) => ({
                    id_produto: p.product.id as number,
                    preco_compra: p.price,
                    preco_cif: p.cifPrice,
                    quantidade_produto: Number(p.qnt) || 1,
                })),
                buyInfo.coin?.id
            )

            const newProducts = buyInfo.products.map((p) => {
                const calc = calcResults.find(
                    (r) => r.id_produto === p.product.id
                )
                if (!calc) return p

                const porc = Number(p.porcentGain) || 0
                const salePrice = currency(calc.finalBuyPrice)
                    .add(
                        currency(calc.finalBuyPrice)
                            .multiply(porc)
                            .divide(100)
                    )
                    .value

                return { ...p, salePrice }
            })

            setBuyInfo((prev) => ({ ...prev, products: newProducts }))
            snack.success('Preços de venda calculados!')
        } catch (e) {
            snack.connectionFail(e)
        } finally {
            loading.logicHide()
        }
    }

    const handleChangeCoin = (coin: CoinInfo) => {
        const newProducts = [...buyInfo.products].map((product) =>
            calculateExchange(product, coin)
        )

        setBuyInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
            coin: coin,
        }))
    }

    const handleLoadExcelInPreSale = async (base64: string) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch
        if (isMasterBranch && !buyInfo.branch) {
            snack.error('Seleccione una sucursal!')
            return
        }

        loading.show()
        try {
            const { products, notFoundProducts } = await loadProductsFromExcel(
                base64,
                coins,
                buyInfo.branch?.id,
            )

            const newProducts = [...buyInfo.products]
            let count = buyInfo.productCount ?? 0

            products.forEach((product) => {
                const foundProduct = buyInfo.products.find(
                    (p) => p.product.id === product.product.id
                )
                if (foundProduct && foundProduct.product.autoSum) {
                    handleChangeProductQnt(
                        foundProduct,
                        currency(foundProduct.qnt).add(1).value
                    )
                    return
                }
                if (!product.product.isAtive) {
                    snack.error('O produto está desativado!')
                    return
                }

                count += 1
                newProducts.push(
                    calculateExchange(
                        {
                            product: product.product,
                            price: product.product.buyPrice,
                            cifPrice: 0,
                            exchange: null,
                            qnt: 1,
                            subtotal: product.product.buyPrice,
                            salePrice: product.product.salePrice,
                            porcentGain: product.product.porcentGain,
                            discount: 0,
                            id: count,
                        },
                        buyInfo.coin!
                    )
                )
            })

            setBuyInfo((prev) => ({
                ...prev,
                products: newProducts,
                total: getTotal(newProducts),
                totalIva: getTotalIva(newProducts),
                productCount: count,
            }))
            /* if (notFoundProducts.length)
                    setNotFoundProductsInExcel(notFoundProducts) */
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    return (
        <buyContext.Provider
            value={{
                buyInfo,
                setBuyInfo,
                handleChangeBill,
                handleAddNewProducts,
                handleRemoveProduct,
                handleChangeProductQnt,
                handleChangeProductDiscount,
                handleChangeProductPrice,
                handleChangeProductSubtotal,
                handleChangeProductExchange,
                handleEditProduct,
                handleChangeSupplier,
                handleChangePayment,
                handleChangeBranch,
                validate,
                handleCreateNewBuy,
                handleChangeSelectedProducts,
                resetContext,
                editBuy,
                handleCalculatePriceWithStock,
                handleCalculatePriceWithCost,
                handleCalculateSalePrices,
                handleChangeCoin,
                handleLoadExcelInPreSale,
            }}
        >
            {children}
        </buyContext.Provider>
    )
}
